import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchShopCategories,
  fetchShopProducts,
  formatShopPrice,
} from "./shopApi";
import AdPlacement from "../Advertising/AdPlacement";
import { prefetchPlacements } from "../Advertising/advertisingApi";
import { detectDeviceClass } from "../Advertising/providers/AdProviderResolver";
import {
  DISPLAY_AD_SIZE_ATTRIBUTES,
  readDisplayAdSize,
} from "../Advertising/displayAdSize";
import "./ShopCatalog.css";

const SHOP_SIDEBAR_PLACEMENT_KEYS = [
  "shop_sidebar_top",
  "shop_sidebar_mid",
  "shop_sidebar_bottom",
];

/** Client-side browse modes (A-006.3B). Not encoded in the URL. */
export const BROWSE_MODE = {
  ALL: "ALL",
  FEATURED: "FEATURED",
  CATEGORY: "CATEGORY",
};

function createBrowseModeAll() {
  return { type: BROWSE_MODE.ALL };
}

function createBrowseModeFeatured() {
  return { type: BROWSE_MODE.FEATURED };
}

function createBrowseModeCategory(categorySlug) {
  return { type: BROWSE_MODE.CATEGORY, categorySlug };
}

function browseModeKey(mode) {
  if (!mode) return BROWSE_MODE.ALL;
  if (mode.type === BROWSE_MODE.CATEGORY) {
    return `${BROWSE_MODE.CATEGORY}:${mode.categorySlug || ""}`;
  }
  return mode.type || BROWSE_MODE.ALL;
}

function dedupeById(products) {
  const seen = new Set();
  const out = [];
  (products || []).forEach((product) => {
    if (!product || product.id == null || seen.has(product.id)) {
      return;
    }
    seen.add(product.id);
    out.push(product);
  });
  return out;
}

/** Depth-first flatten preserving API sort_order within each level. */
function flattenCategories(nodes) {
  const out = [];
  (nodes || []).forEach((node) => {
    if (!node) return;
    out.push(node);
    if (node.children && node.children.length) {
      out.push(...flattenCategories(node.children));
    }
  });
  return out;
}

function getHeaderHeight() {
  const header = document.getElementById("header");
  if (header) {
    return header.getBoundingClientRect().height || 0;
  }
  return 64;
}

function scrollShopToTop(catalogEl) {
  if (!catalogEl || typeof window === "undefined") return;
  const offset = getHeaderHeight() + 8;
  const y =
    catalogEl.getBoundingClientRect().top +
    (window.pageYOffset || window.scrollY || 0) -
    offset;
  window.scrollTo({
    top: Math.max(0, Math.round(y)),
    behavior: "smooth",
  });
}

/** Keep the active pill visible inside the horizontal nav without vertical page scroll. */
function scrollNavPillIntoView(navEl, buttonEl) {
  if (!navEl || !buttonEl) return;
  const navRect = navEl.getBoundingClientRect();
  const btnRect = buttonEl.getBoundingClientRect();
  const delta =
    btnRect.left -
    navRect.left -
    (navRect.width - btnRect.width) / 2;
  navEl.scrollBy({ left: delta, behavior: "smooth" });
}

const SHOP_SIDEBAR_AD_MAX_SCALE = 1;
const SHOP_SIDEBAR_AD_SLOTS = ["top", "mid", "bottom"];

/**
 * Fits each sidebar placement's creative (its served size, else the selected
 * unit — see readDisplayAdSize) to the column width: aspect ratio kept, never
 * upscaled. Vars are written on the aside per slot (--shop-ad-<top|mid|bottom>-*)
 * and mapped in ShopCatalog.css.
 */
function useShopSidebarAdFit(hostRef, enabled) {
  useEffect(() => {
    const host = hostRef.current;
    if (
      !enabled ||
      !host ||
      typeof window === "undefined" ||
      typeof window.ResizeObserver !== "function"
    ) {
      return undefined;
    }
    const setVar = (prop, value) => {
      if (host.style.getPropertyValue(prop) !== value) host.style.setProperty(prop, value);
    };
    const sync = () => {
      host.querySelectorAll(".display-ad[data-ad-width][data-ad-height]").forEach((unit) => {
        const slot = (unit.getAttribute("data-placement") || "").replace(/^shop_sidebar_/, "");
        if (SHOP_SIDEBAR_AD_SLOTS.indexOf(slot) === -1) return;
        const size = readDisplayAdSize(unit);
        const unitWidth = size ? size.width : 0;
        const unitHeight = size ? size.height : 0;
        const width = unit.getBoundingClientRect().width;
        if (!(unitWidth > 0 && unitHeight > 0 && width > 0)) return;
        // Rounded down so the scaled creative never exceeds the fractional box.
        const scale = Math.min(SHOP_SIDEBAR_AD_MAX_SCALE, Math.floor((width / unitWidth) * 10000) / 10000);
        const prefix = `--shop-ad-${slot}`;
        setVar(`${prefix}-fit-width`, `${Math.floor(unitWidth * scale * 100) / 100}px`);
        setVar(`${prefix}-fit-height`, `${Math.floor(unitHeight * scale * 100) / 100}px`);
        setVar(`${prefix}-width`, `${unitWidth}px`);
        setVar(`${prefix}-height`, `${unitHeight}px`);
        setVar(`${prefix}-ratio`, `${unitWidth} / ${unitHeight}`);
        setVar(`${prefix}-scale`, scale.toFixed(4));
      });
    };
    const resizeObserver = new window.ResizeObserver(sync);
    resizeObserver.observe(host);
    const unitObserver =
      typeof window.MutationObserver === "function" ? new window.MutationObserver(sync) : null;
    if (unitObserver) {
      unitObserver.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: DISPLAY_AD_SIZE_ATTRIBUTES,
      });
    }
    sync();
    return () => {
      resizeObserver.disconnect();
      if (unitObserver) unitObserver.disconnect();
    };
  }, [hostRef, enabled]);
}

const SHOP_SIDEBAR_STICKY_TOP_REM = 5.5;
const SHOP_SIDEBAR_STICKY_HEADER_GAP = 8;
const SHOP_SIDEBAR_STICKY_BOTTOM_GAP = 16;

/**
 * Sticky offset for the sidebar (--shop-sidebar-sticky-top): pinned below the
 * global header while it fits the viewport; when taller (stacked MPU +
 * skyscraper + half-page) it pins by its bottom edge instead, so the lower
 * placements are reachable rather than held below the fold.
 */
function useShopSidebarStickyTop(hostRef, enabled) {
  useEffect(() => {
    const host = hostRef.current;
    if (!enabled || !host || typeof window === "undefined") return undefined;
    const sync = () => {
      const rootFont =
        parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const preferred = Math.max(
        SHOP_SIDEBAR_STICKY_TOP_REM * rootFont,
        getHeaderHeight() + SHOP_SIDEBAR_STICKY_HEADER_GAP
      );
      const viewport = document.documentElement.clientHeight || window.innerHeight || 0;
      const height = host.offsetHeight;
      const top =
        viewport > 0 && preferred + height + SHOP_SIDEBAR_STICKY_BOTTOM_GAP > viewport
          ? viewport - height - SHOP_SIDEBAR_STICKY_BOTTOM_GAP
          : preferred;
      const value = `${Math.round(top)}px`;
      if (host.style.getPropertyValue("--shop-sidebar-sticky-top") !== value) {
        host.style.setProperty("--shop-sidebar-sticky-top", value);
      }
    };
    const resizeObserver =
      typeof window.ResizeObserver === "function" ? new window.ResizeObserver(sync) : null;
    if (resizeObserver) resizeObserver.observe(host);
    window.addEventListener("resize", sync);
    sync();
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [hostRef, enabled]);
}

function ProductCard({ product }) {
  const priceLabel = product.is_free
    ? "Free"
    : formatShopPrice(product.effective_price);
  const alt = product.name || "Product";

  return (
    <li className="shop-catalog__card">
      <Link
        to={`/shop/product/${encodeURIComponent(product.slug)}`}
        className="shop-catalog__link"
      >
        <div className="shop-catalog__media">
          {product.primary_image ? (
            <img
              src={product.primary_image}
              alt={alt}
              loading="lazy"
              decoding="async"
              width={280}
              height={280}
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.parentElement) {
                  e.target.parentElement.classList.add(
                    "shop-catalog__media--missing"
                  );
                }
              }}
            />
          ) : (
            <div
              className="shop-catalog__placeholder"
              role="img"
              aria-label={alt}
            />
          )}
        </div>
        <div className="shop-catalog__body">
          <h3 className="shop-catalog__title">{product.name}</h3>
          {product.short_description ? (
            <p className="shop-catalog__desc">{product.short_description}</p>
          ) : null}
          {priceLabel ? (
            <p className="shop-catalog__price">
              {product.is_on_sale &&
              product.regular_price != null &&
              !product.is_free ? (
                <span className="shop-catalog__price-was">
                  {formatShopPrice(product.regular_price)}
                </span>
              ) : null}
              <span>{priceLabel}</span>
            </p>
          ) : null}
        </div>
      </Link>
    </li>
  );
}

function ProductRow({ title, products, sectionId, focused }) {
  if (!products || !products.length) {
    return null;
  }
  return (
    <section
      id={sectionId}
      className={`shop-catalog__row${
        focused ? " shop-catalog__row--focused" : ""
      }`}
      aria-labelledby={`${sectionId}-heading`}
      data-shop-section={sectionId}
    >
      <div className="shop-catalog__row-head">
        <h2 id={`${sectionId}-heading`} className="shop-catalog__heading">
          {title}
        </h2>
      </div>
      <ul
        className={`shop-catalog__rail${
          focused ? " shop-catalog__rail--focused" : ""
        }`}
      >
        {products.map((product) => (
          <ProductCard key={`${sectionId}-${product.id}`} product={product} />
        ))}
      </ul>
    </section>
  );
}

function BrowseEmptyState({ title }) {
  return (
    <div className="shop-catalog__empty" role="status">
      <h2 className="shop-catalog__heading">{title || "Products"}</h2>
      <p>No products are currently available in this category.</p>
    </div>
  );
}

/**
 * A-006.3B — Hybrid Shop browse with mode selection:
 * ALL | FEATURED | CATEGORY(slug)
 *
 * Pattern is reusable for future Community browse (orgs/creators/projects).
 */
function ShopCatalog() {
  const [featured, setFeatured] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [browseMode, setBrowseMode] = useState(createBrowseModeAll);
  const catalogRef = useRef(null);
  const navRef = useRef(null);
  const contentRef = useRef(null);
  const sidebarRef = useRef(null);
  useShopSidebarAdFit(sidebarRef, !loading);
  useShopSidebarStickyTop(sidebarRef, !loading);

  // One batched placements request in parallel with the catalog; the sidebar
  // AdPlacements (same keys / device / limit) consume it when they mount.
  useEffect(() => {
    prefetchPlacements(SHOP_SIDEBAR_PLACEMENT_KEYS, {
      device: detectDeviceClass(),
      limit: 1,
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [featuredResult, categoryList] = await Promise.all([
          fetchShopProducts({ featured: "1", per_page: 24 }),
          fetchShopCategories(),
        ]);
        if (cancelled) return;

        const featuredProducts = dedupeById(featuredResult.products || []);
        const flatCategories = flattenCategories(categoryList || []);

        setFeatured(featuredProducts);

        const rowResults = await Promise.all(
          flatCategories.map(async (category) => {
            const result = await fetchShopProducts({
              category_slug: category.slug,
              per_page: 24,
            });
            return {
              category,
              products: dedupeById(result.products || []),
            };
          })
        );

        if (!cancelled) {
          // Keep empty categories for focused empty-state; hide their rails in ALL.
          setRows(rowResults.filter((row) => row.category));
          setBrowseMode(createBrowseModeAll());
        }
      } catch (e) {
        if (!cancelled) {
          setFeatured([]);
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rowsWithProducts = useMemo(
    () => rows.filter((row) => row.products && row.products.length > 0),
    [rows]
  );

  const modeKey = browseModeKey(browseMode);

  useEffect(() => {
    if (!navRef.current) return;
    const btn = navRef.current.querySelector(
      `.shop-catalog__nav-btn[data-browse-key="${modeKey}"]`
    );
    scrollNavPillIntoView(navRef.current, btn);
  }, [modeKey]);

  const selectBrowseMode = (nextMode) => {
    setBrowseMode(nextMode);
    scrollShopToTop(catalogRef.current);
    // Move focus into the content region for keyboard / screen reader users.
    window.setTimeout(() => {
      if (contentRef.current) {
        try {
          contentRef.current.focus({ preventScroll: true });
        } catch (e) {
          // ignore
        }
      }
    }, 50);
  };

  const visibleContent = useMemo(() => {
    const mode = browseMode || createBrowseModeAll();

    if (mode.type === BROWSE_MODE.FEATURED) {
      return {
        showFeatured: true,
        featuredFocused: true,
        categoryRows: [],
        emptyTitle: featured.length ? null : "Featured",
        empty: featured.length === 0,
      };
    }

    if (mode.type === BROWSE_MODE.CATEGORY) {
      const slug = mode.categorySlug;
      const row = rows.find((r) => r.category && r.category.slug === slug);
      const products = row ? row.products : [];
      const title = row && row.category ? row.category.name : "Category";
      return {
        showFeatured: false,
        featuredFocused: false,
        categoryRows:
          products.length > 0
            ? [{ category: row.category, products, focused: true }]
            : [],
        emptyTitle: title,
        empty: products.length === 0,
      };
    }

    // ALL
    return {
      showFeatured: featured.length > 0,
      featuredFocused: false,
      categoryRows: rowsWithProducts.map((row) => ({
        ...row,
        focused: false,
      })),
      emptyTitle: null,
      empty: false,
    };
  }, [browseMode, featured, rows, rowsWithProducts]);

  if (loading) {
    return (
      <section className="shop-catalog" aria-busy="true">
        <div className="shop-catalog__inner">
          <div className="shop-catalog__loading" role="status">
            <span className="shop-catalog__loading-bar" aria-hidden="true" />
            <p>Loading…</p>
          </div>
        </div>
      </section>
    );
  }

  const hasNavContent = featured.length > 0 || rows.length > 0;
  if (!hasNavContent) {
    return null;
  }

  const isAll = browseMode.type === BROWSE_MODE.ALL;
  const isFeatured = browseMode.type === BROWSE_MODE.FEATURED;

  return (
    <section
      className="shop-catalog"
      aria-label="Shop products"
      ref={catalogRef}
    >
      <div className="shop-catalog__layout">
        <div className="shop-catalog__inner">
          <nav
            className="shop-catalog__nav"
            ref={navRef}
            aria-label="Shop browse modes"
          >
            <button
              type="button"
              className={`shop-catalog__nav-btn${isAll ? " is-active" : ""}`}
              data-browse-key={BROWSE_MODE.ALL}
              onClick={() => selectBrowseMode(createBrowseModeAll())}
              aria-pressed={isAll}
            >
              All
            </button>
            {featured.length > 0 ? (
              <button
                type="button"
                className={`shop-catalog__nav-btn${
                  isFeatured ? " is-active" : ""
                }`}
                data-browse-key={BROWSE_MODE.FEATURED}
                onClick={() => selectBrowseMode(createBrowseModeFeatured())}
                aria-pressed={isFeatured}
              >
                Featured
              </button>
            ) : null}
            {rows.map(({ category }) => {
              const key = browseModeKey(createBrowseModeCategory(category.slug));
              const isActive =
                browseMode.type === BROWSE_MODE.CATEGORY &&
                browseMode.categorySlug === category.slug;
              return (
                <button
                  type="button"
                  key={category.id}
                  className={`shop-catalog__nav-btn${
                    isActive ? " is-active" : ""
                  }`}
                  data-browse-key={key}
                  onClick={() =>
                    selectBrowseMode(createBrowseModeCategory(category.slug))
                  }
                  aria-pressed={isActive}
                >
                  {category.name}
                </button>
              );
            })}
          </nav>

          <div
            className="shop-catalog__stage"
            key={modeKey}
            ref={contentRef}
            tabIndex={-1}
            aria-live="polite"
          >
            {visibleContent.empty ? (
              <BrowseEmptyState title={visibleContent.emptyTitle} />
            ) : (
              <>
                {visibleContent.showFeatured ? (
                  <ProductRow
                    title="Featured"
                    products={featured}
                    sectionId="shop-section-featured"
                    focused={visibleContent.featuredFocused}
                  />
                ) : null}

                {visibleContent.categoryRows.map(
                  ({ category, products, focused }) => (
                    <ProductRow
                      key={category.id}
                      title={category.name}
                      products={products}
                      sectionId={`shop-section-${category.slug}`}
                      focused={!!focused}
                    />
                  )
                )}
              </>
            )}
          </div>
        </div>
        <aside
          className="shop-catalog__sidebar"
          aria-label="Shop promotions"
          ref={sidebarRef}
        >
          <AdPlacement placementKey="shop_sidebar_top" variant="sidebar" limit={1} />
          <AdPlacement placementKey="shop_sidebar_mid" variant="sidebar" limit={1} />
          <AdPlacement placementKey="shop_sidebar_bottom" variant="sidebar" limit={1} />
        </aside>
      </div>
    </section>
  );
}

export default ShopCatalog;
